import axios from "axios";
import initialData from "./initialData.json";
import listingsData from "./listingsData.json";
import deletedSlugsList from "./deletedSlugs.json";
import { STATES, slugifyCity, slugify } from "./constants";

// Determine active backend URL
function determineBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (!envUrl || envUrl.trim() === "" || envUrl === "undefined") {
    return "";
  }
  if (typeof window !== "undefined" && window.location) {
    const hostname = window.location.hostname || "";
    const isLocalhostHost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".local");
    // If deployed on HTTPS production (e.g. Hostinger) and envUrl points to localhost, ignore it to prevent mixed-content errors
    if (!isLocalhostHost && envUrl.includes("localhost")) {
      return "";
    }
  }
  return envUrl.replace(/\/$/, "");
}

const BACKEND_URL = determineBackendUrl();
let hasBackend = Boolean(BACKEND_URL);
let backendUnavailableUntil = 0;

const client = axios.create({
  baseURL: hasBackend ? `${BACKEND_URL}/api` : "/api",
  timeout: 1200,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function markBackendFailure() {
  backendUnavailableUntil = Date.now() + 60000;
}

function isBackendAvailable() {
  return hasBackend && Date.now() > backendUnavailableUntil;
}

// Local storage keys
const COURSES_KEY = "minigolf_courses";
const BLOGS_KEY = "minigolf_blogs";
const CONTENT_KEY = "minigolf_content";
const CITY_CONTENTS_KEY = "minigolf_city_contents";
const STATE_CONTENTS_KEY = "minigolf_state_contents";
const ABOUT_KEY = "minigolf_about_content";
const CONTACT_KEY = "minigolf_contact_content";
const CONTACT_MSGS_KEY = "minigolf_contact_messages";
const PRIVACY_KEY = "minigolf_privacy_content";
const TERMS_KEY = "minigolf_terms_content";
const SEO_SETTINGS_KEY = "minigolf_seo_settings";
const AUTH_KEY = "minigolf_auth_user";

export const DELETED_COURSE_SLUGS = new Set(deletedSlugsList);

export const DEFAULT_PRIVACY_CONTENT = {
  title: "Privacy Policy",
  lastUpdated: "September 2026",
  contactEmail: "privacy@minigolfalabama.com",
  introParagraphs: [
    'Welcome to Mini Golf Alabama ("we," "our," or "us"), accessible via minigolfalabama.com. We value your privacy and are committed to protecting the personal information you may share while browsing our directory of mini golf and putt-putt courses across the United States.',
    'This Privacy Policy explains what information we collect, how we use it, how we safeguard it, and your rights regarding your data when you visit our website.'
  ],
  sections: [
    {
      title: "1. Information We Collect",
      content: "When you visit our directory, our servers automatically record standard browser data, such as your IP address, browser type, operating system, referring URLs, and pages viewed. We also collect information you provide directly when contacting us."
    },
    {
      title: "2. Cookies and Tracking Technologies",
      content: "We use essential session storage and local storage to store user interface preferences and cache directory listings for instant page loading. We do not sell personal data to third parties."
    },
    {
      title: "3. Third-Party Links & External Sites",
      content: "Our directory provides links to official course websites, booking portals, and social pages. We encourage you to review their individual privacy practices."
    },
    {
      title: "4. Data Security & Storage",
      content: "We implement industry-standard encryption, SSL/TLS protocols, and secure cloud storage to protect your data from unauthorized access or disclosure."
    }
  ]
};

export const DEFAULT_TERMS_CONTENT = {
  title: "Terms & Conditions",
  lastUpdated: "September 2026",
  contactEmail: "support@minigolfalabama.com",
  introParagraphs: [
    'By accessing and using minigolfalabama.com (the "Website" or "Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use or access the Website.',
    'Mini Golf Alabama provides an informational directory for discovering miniature golf, putt-putt, indoor golf lounges, and family fun destinations across Alabama and all 50 states.'
  ],
  sections: [
    {
      title: "1. Directory Information & Verification Disclaimer",
      content: "While we strive to keep all course information, hours of operation, pricing, ratings, phone numbers, and addresses accurate and current, details can change without notice due to seasonal schedules, holidays, weather conditions, or venue policy updates. We strongly advise verifying operating hours and pricing directly with the venue before traveling."
    },
    {
      title: "2. Permitted Use & Intellectual Property",
      content: "You are granted a non-exclusive, revocable license to access and browse our directory for personal, non-commercial purposes. Reproduction or scraping of directory content without prior written consent is strictly prohibited."
    },
    {
      title: "3. User Submissions & Inquiries",
      content: "When submitting contact messages or listing updates, you agree to provide truthful and accurate information."
    },
    {
      title: "4. Limitation of Liability",
      content: "Under no circumstances shall Mini Golf Alabama or its operators be liable for any direct, indirect, incidental, or consequential damages resulting from your use of the Website."
    }
  ]
};

export const DEFAULT_SEO_SETTINGS = {
  siteName: "Mini Golf USA",
  siteUrl: "https://minigolfalabama.com",
  defaultTitle: "Mini Golf Alabama | Best Indoor & Outdoor Mini Golf Near Me",
  defaultDescription: "Find a fun and exciting mini golf putt putt course near me in Alabama and across the USA for family outings, date nights, and friendly competition.",
  defaultOgImage: "https://minigolfalabama.com/logo.png",
  logoUrl: "https://minigolfalabama.com/logo.png",
  googleSiteVerification: "bi4Dgit8tyad33aIxnsWe3v0F_ufmeXoIziR7ybwYjU",
  googleAnalyticsId: "",
  contactEmail: "contact@minigolfalabama.com",
  contactPhone: "(800) 555-GOLF",
  socialFacebook: "https://facebook.com",
  socialTwitter: "https://twitter.com",
  socialInstagram: "https://instagram.com",
  socialYoutube: "",
  customHeadJsonLd: "",
  enableWebsiteSchema: true,
  enableOrganizationSchema: true,
  enableCourseSchema: true,
  enableFaqSchema: true,
  enableBreadcrumbSchema: true,
  enableCollectionSchema: true,
  enableBlogSchema: true,
};

export function getLocalSeoSettings() {
  try {
    const raw = localStorage.getItem(SEO_SETTINGS_KEY);
    if (raw) return { ...DEFAULT_SEO_SETTINGS, ...JSON.parse(raw) };
  } catch (e) {}
  return DEFAULT_SEO_SETTINGS;
}

export function saveLocalSeoSettings(data) {
  localStorage.setItem(SEO_SETTINGS_KEY, JSON.stringify(data));
}

export function getLocalCourses() {
  try {
    const raw = localStorage.getItem(COURSES_KEY);
    if (raw !== null && raw !== undefined) {
      const stored = JSON.parse(raw);
      if (Array.isArray(stored)) {
        return stored;
      }
    }
  } catch (e) {}
  return initialData.courses || [];
}

export function saveLocalCourses(courses) {
  localStorage.setItem(COURSES_KEY, JSON.stringify(courses || []));
}

export function getLocalBlogs() {
  try {
    const raw = localStorage.getItem(BLOGS_KEY);
    if (raw !== null && raw !== undefined) {
      const stored = JSON.parse(raw);
      if (Array.isArray(stored)) return stored;
    }
  } catch (e) {}
  return initialData.blogs || [];
}

export function saveLocalBlogs(blogs) {
  localStorage.setItem(BLOGS_KEY, JSON.stringify(blogs || []));
}

export function getLocalContent() {
  try {
    const raw = localStorage.getItem(CONTENT_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return initialData.content || {};
}

export function saveLocalContent(content) {
  localStorage.setItem(CONTENT_KEY, JSON.stringify(content));
}

export function getLocalPrivacyContent() {
  try {
    const raw = localStorage.getItem(PRIVACY_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return DEFAULT_PRIVACY_CONTENT;
}

export function saveLocalPrivacyContent(data) {
  localStorage.setItem(PRIVACY_KEY, JSON.stringify(data));
}

export function getLocalTermsContent() {
  try {
    const raw = localStorage.getItem(TERMS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return DEFAULT_TERMS_CONTENT;
}

export function saveLocalTermsContent(data) {
  localStorage.setItem(TERMS_KEY, JSON.stringify(data));
}

export function getLocalCityContents() {
  try {
    const raw = localStorage.getItem(CITY_CONTENTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return initialData.cityContents || [];
}

export function saveLocalCityContents(contents) {
  localStorage.setItem(CITY_CONTENTS_KEY, JSON.stringify(contents));
}

export function getLocalStateContents() {
  try {
    const raw = localStorage.getItem(STATE_CONTENTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return initialData.stateContents || [];
}

export function saveLocalStateContents(contents) {
  localStorage.setItem(STATE_CONTENTS_KEY, JSON.stringify(contents));
}

export function getLocalAboutContent() {
  try {
    const raw = localStorage.getItem(ABOUT_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return initialData.aboutContent || {};
}

export function saveLocalAboutContent(data) {
  localStorage.setItem(ABOUT_KEY, JSON.stringify(data));
}

export function getLocalContactContent() {
  try {
    const raw = localStorage.getItem(CONTACT_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return initialData.contactContent || {};
}

export function saveLocalContactContent(data) {
  localStorage.setItem(CONTACT_KEY, JSON.stringify(data));
}

export function getLocalContactMessages() {
  try {
    const raw = localStorage.getItem(CONTACT_MSGS_KEY);
    if (raw !== null && raw !== undefined) {
      const stored = JSON.parse(raw);
      if (Array.isArray(stored)) return stored;
    }
  } catch (e) {}
  return initialData.contactMessages || [];
}

export function saveLocalContactMessages(msgs) {
  localStorage.setItem(CONTACT_MSGS_KEY, JSON.stringify(msgs || []));
}

export function generateDefaultCityContent(stateName, cityName, courses = []) {
  const count = courses.length || 0;
  const avg = count > 0
    ? (courses.reduce((acc, c) => acc + Number(c.rating || 0), 0) / count).toFixed(1)
    : "4.5";
  const sorted = [...courses].sort((a, b) => (b.rating - a.rating) || (b.reviewCount - a.reviewCount));
  const topCourse = sorted[0];
  const topText = topCourse
    ? ` ${topCourse.name} is the top-rated spot, earning ${topCourse.rating} stars from players.`
    : "";

  return {
    cityName,
    customTitle: `Mini Golf Courses in ${cityName}, ${stateName}`,
    leadParagraph: `Looking for mini golf in ${cityName}, ${stateName}? Browse ${count} ${count === 1 ? "course" : "courses"} with an average rating of ${avg} stars.${topText}`,
    contentParagraphs: [
      `Mini golf in ${cityName} offers a fantastic variety of fun for families, friends, date nights, and competitive putters. Across the area, you'll find everything from classic obstacles and windmill challenges to elaborately themed adventure greens, waterfalls, and climate-controlled indoor layouts that let you putt comfortably in any season. Our directory lists ${count} top-rated mini golf spots around ${cityName} with an average visitor rating of ${avg} stars based on player reviews. Browse the full list below to compare course photos, ratings, hours, and locations before planning your visit.`
    ],
    isAutoGenerated: true,
  };
}

export function generateDefaultStateContent(stateName, courses = []) {
  const count = courses.length || 0;
  const citiesCount = new Set(courses.map((c) => c.city).filter(Boolean)).size;
  const avg = count > 0
    ? (courses.reduce((acc, c) => acc + Number(c.rating || 0), 0) / count).toFixed(1)
    : "4.5";
  const sorted = [...courses].sort((a, b) => (b.rating - a.rating) || (b.reviewCount - a.reviewCount));
  const topCourse = sorted[0];
  const topText = topCourse
    ? ` ${topCourse.name} leads the state with a ${topCourse.rating}-star rating from visitor reviews.`
    : "";

  return {
    customTitle: `Mini Golf Courses in ${stateName}`,
    leadParagraph: `Looking for the best mini golf courses in ${stateName}? Explore ${count} top-rated putt putt locations across ${citiesCount > 0 ? `${citiesCount} cities` : "the state"} with an average visitor rating of ${avg} stars.${topText}`,
    contentParagraphs: [
      `Whether you're planning a family weekend outing, a memorable date night, or looking for indoor glowing blacklight courses, ${stateName} offers a diverse range of miniature golf adventures. From coastal outdoor courses featuring pirate ships and cascading waterfalls to climate-controlled indoor fun centers with arcade games and snacks, there's a course for every skill level.`,
      `Browse our comprehensive state directory below to compare player ratings, admission prices, course hours, and photos before heading out to play.`
    ],
    isAutoGenerated: true,
  };
}

export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
};

// Synchronous Fast Getters (Instant 0ms UI Rendering)
export const getCourseSync = (idOrSlug) => {
  if (!idOrSlug) return null;
  const normalized = String(idOrSlug || "").toLowerCase();
  if (DELETED_COURSE_SLUGS.has(normalized) || DELETED_COURSE_SLUGS.has(slugify(normalized))) {
    return null;
  }
  const courses = getLocalCourses();
  let found = courses.find(
    (x) =>
      x.id === idOrSlug ||
      x.slug === idOrSlug ||
      slugify(x.name) === normalized ||
      x.name.toLowerCase() === normalized.replace(/-/g, " ") ||
      slugify(x.name) === slugify(idOrSlug)
  );
  if (!found && initialData && Array.isArray(initialData.courses)) {
    found = initialData.courses.find(
      (x) =>
        x.id === idOrSlug ||
        x.slug === idOrSlug ||
        slugify(x.name) === normalized ||
        x.name.toLowerCase() === normalized.replace(/-/g, " ") ||
        slugify(x.name) === slugify(idOrSlug)
    );
  }
  return found || null;
};

export const getCoursesSync = (params = {}) => {
  let list = [...getLocalCourses()];
  const { state, city, featured, sort, limit, q } = params;

  if (state) {
    const stObj = STATES.find(
      (s) =>
        s.code === state.toLowerCase() ||
        s.name.toLowerCase() === state.toLowerCase().replace(/-/g, " ") ||
        slugify(s.name) === state.toLowerCase()
    );
    const targetCode = stObj ? stObj.code : state.toLowerCase();
    list = list.filter(
      (c) => c.state.toLowerCase() === targetCode || c.state.toLowerCase() === state.toLowerCase()
    );
  }
  if (city) {
    list = list.filter(
      (c) =>
        c.citySlug === city ||
        slugifyCity(c.city) === city ||
        c.city.toLowerCase() === city.toLowerCase()
    );
  }
  if (featured === true || featured === "true") {
    list = list.filter((c) => c.featured);
  }
  if (q && q.trim()) {
    const term = q.trim().toLowerCase();
    list = list.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.city.toLowerCase().includes(term) ||
        (c.description && c.description.toLowerCase().includes(term))
    );
  }
  if (sort === "top") {
    list.sort((a, b) => b.rating - a.rating || b.reviewCount - a.reviewCount);
  }
  if (limit) {
    list = list.slice(0, parseInt(limit, 10));
  }
  return list;
};

export const getStatesSync = () => {
  const courses = getLocalCourses();
  const map = {};
  STATES.forEach((s) => {
    map[s.code] = { code: s.code, name: s.name, count: 0, cities: new Set() };
  });
  courses.forEach((c) => {
    if (map[c.state]) {
      map[c.state].count++;
      if (c.city) map[c.state].cities.add(c.city);
    }
  });
  (listingsData || []).forEach((l) => {
    const code = (l.stateCode || "").toLowerCase();
    if (map[code] && l.cityName) {
      map[code].cities.add(l.cityName);
    }
  });
  return Object.values(map).map((s) => ({
    ...s,
    cities: Array.from(s.cities).sort(),
  }));
};

export const getPopularCitiesSync = () => {
  const courses = getLocalCourses();
  const map = {};
  courses.forEach((c) => {
    const key = `${c.citySlug || slugifyCity(c.city)}|${c.state}`;
    if (!map[key]) {
      map[key] = {
        city: c.city,
        state: c.state,
        slug: c.citySlug || slugifyCity(c.city),
        courses: 0,
        ratingSum: 0,
      };
    }
    map[key].courses += 1;
    map[key].ratingSum += c.rating;
  });
  const results = Object.values(map)
    .map((item) => ({
      city: item.city,
      state: item.state,
      slug: item.slug,
      courses: item.courses,
      avgRating: parseFloat((item.ratingSum / item.courses).toFixed(1)),
    }))
    .sort((a, b) => b.courses - a.courses || b.avgRating - a.avgRating)
    .slice(0, 12);

  if (results.length > 0) return results;
  return [];
};

// Login
export const login = async (username, password) => {
  if (isBackendAvailable()) {
    try {
      const res = await client.post("/auth/login", { username, password });
      return res.data;
    } catch (e) {
      if (e.response && (e.response.status === 401 || e.response.status === 429)) {
        throw e;
      }
      markBackendFailure();
    }
  }
  if (username.toLowerCase() === "admin" && password === "Ashu!@#123") {
    const user = { id: "local-admin", username: "admin", role: "admin" };
    const token = "mock-jwt-token-" + Date.now();
    localStorage.setItem("admin_token", token);
    localStorage.setItem(AUTH_KEY, JSON.stringify(user));
    return { token, user };
  }
  const err = new Error("Invalid username or password");
  err.response = { data: { detail: "Invalid username or password" } };
  throw err;
};

// Me
export const me = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/auth/me");
      return res.data;
    } catch (e) {
      if (e.response && e.response.status === 401) throw e;
      markBackendFailure();
    }
  }
  const token = localStorage.getItem("admin_token");
  if (!token) {
    const err = new Error("Not authenticated");
    err.response = { status: 401, data: { detail: "Not authenticated" } };
    throw err;
  }
  const raw = localStorage.getItem(AUTH_KEY);
  return raw ? JSON.parse(raw) : { id: "local-admin", username: "admin", role: "admin" };
};

// States
export const getStates = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/states");
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getStatesSync();
};

// Courses with filtering
export const getCourses = async (params = {}) => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/courses", { params: { limit: 100000, ...params } });
      if (res.data && Array.isArray(res.data)) {
        if (!params || Object.keys(params).length === 0) {
          saveLocalCourses(res.data);
        }
      }
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getCoursesSync(params);
};

// Single Course
export const getCourse = async (idOrSlug) => {
  const normalized = String(idOrSlug || "").toLowerCase();
  if (DELETED_COURSE_SLUGS.has(normalized) || DELETED_COURSE_SLUGS.has(slugify(normalized))) {
    const err = new Error("Course not found");
    err.response = { status: 404, data: { detail: "Course not found" } };
    throw err;
  }
  if (isBackendAvailable()) {
    try {
      const res = await client.get(`/courses/${idOrSlug}`);
      if (res.data) {
        const courses = getLocalCourses();
        const idx = courses.findIndex(
          (c) => c.id === res.data.id || c.slug === res.data.slug || slugify(c.name) === slugify(res.data.name)
        );
        if (idx !== -1) {
          courses[idx] = { ...courses[idx], ...res.data };
        } else {
          courses.unshift(res.data);
        }
        saveLocalCourses(courses);
        return res.data;
      }
    } catch (e) {
      if (e.response && e.response.status === 404) {
        // Course not found on backend
      } else {
        markBackendFailure();
      }
    }
  }
  const c = getCourseSync(idOrSlug);
  if (!c) {
    const err = new Error("Course not found");
    err.response = { status: 404, data: { detail: "Course not found" } };
    throw err;
  }
  return c;
};

// Popular Cities
export const getPopularCities = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/popular-cities");
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getPopularCitiesSync();
};

// Content
export const getContent = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/content");
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalContent();
};

// Create Course
export const createCourse = async (data) => {
  const courses = getLocalCourses();
  const slug = slugify(data.name) || slugifyCity(data.name);
  let newCourse = {
    ...data,
    description: (data.description || "").trim(),
    id: "course-" + Date.now() + "-" + Math.random().toString(36).substr(2, 9),
    slug: slug,
    citySlug: slugifyCity(data.city),
    createdAt: new Date().toISOString(),
  };

  if (hasBackend) {
    try {
      const res = await client.post("/courses", data);
      if (res.data) {
        newCourse = res.data;
      }
    } catch (e) {}
  }

  const filtered = courses.filter((c) => c.id !== newCourse.id);
  filtered.unshift(newCourse);
  saveLocalCourses(filtered);
  return newCourse;
};

// Update Course
export const updateCourse = async (id, data) => {
  const courses = getLocalCourses();
  const slug = slugify(data.name) || slugifyCity(data.name);
  let updated = {
    ...data,
    description: (data.description || "").trim(),
    id,
    slug: slug,
    citySlug: slugifyCity(data.city || ""),
    updatedAt: new Date().toISOString(),
  };

  if (hasBackend) {
    try {
      const res = await client.put(`/courses/${id}`, data);
      if (res.data) {
        updated = res.data;
      }
    } catch (e) {}
  }

  const index = courses.findIndex((c) => c.id === id);
  if (index !== -1) {
    courses[index] = { ...courses[index], ...updated };
  } else {
    courses.unshift(updated);
  }
  saveLocalCourses(courses);
  return updated;
};

// Delete Course
export const deleteCourse = async (id) => {
  if (hasBackend) {
    try {
      const res = await client.delete(`/courses/${id}`);
      return res.data;
    } catch (e) {}
  }
  let courses = getLocalCourses();
  courses = courses.filter((c) => c.id !== id);
  saveLocalCourses(courses);
  return { ok: true };
};

// Batch Delete Courses (Select Multiple)
export const deleteCoursesBatch = async (ids) => {
  if (!ids || ids.length === 0) return { ok: true, count: 0 };
  if (hasBackend) {
    try {
      const res = await client.post("/courses-batch-delete", { ids });
      return res.data;
    } catch (e) {
      // Fallback: try individual delete calls
      try {
        await Promise.allSettled(ids.map((id) => client.delete(`/courses/${id}`)));
      } catch (err) {}
    }
  }
  let courses = getLocalCourses();
  const idSet = new Set(ids);
  courses = courses.filter((c) => !idSet.has(c.id));
  saveLocalCourses(courses);
  return { ok: true, count: ids.length };
};

// Delete All Courses
export const deleteAllCourses = async () => {
  let backendResult = null;
  if (hasBackend) {
    try {
      const res = await client.delete("/courses-all");
      backendResult = res.data;
    } catch (e) {}
  }
  saveLocalCourses([]);
  return backendResult || { ok: true };
};

// Update Content
export const updateContent = async (data) => {
  if (hasBackend) {
    try {
      const res = await client.put("/content", data);
      return res.data;
    } catch (e) {}
  }
  saveLocalContent(data);
  return data;
};

// ==================== BLOGS API ====================

export const getBlogsSync = (params = {}) => {
  let list = [...getLocalBlogs()];
  const { category, q, all } = params;

  // Filter unpublished if not admin request
  if (!all) {
    list = list.filter((b) => b.published !== false);
  }

  if (category && category !== "All") {
    list = list.filter((b) => b.category?.toLowerCase() === category.toLowerCase());
  }

  if (q && q.trim()) {
    const term = q.trim().toLowerCase();
    list = list.filter(
      (b) =>
        b.title.toLowerCase().includes(term) ||
        b.excerpt?.toLowerCase().includes(term) ||
        b.content?.toLowerCase().includes(term) ||
        b.author?.toLowerCase().includes(term)
    );
  }

  return list;
};

export const getBlogSync = (idOrSlug) => {
  const blogs = getLocalBlogs();
  const normalized = String(idOrSlug || "").toLowerCase();
  return (
    blogs.find(
      (b) =>
        b.id === idOrSlug ||
        b.slug === idOrSlug ||
        slugify(b.title) === normalized ||
        b.slug?.toLowerCase() === normalized
    ) || null
  );
};

export const getBlogs = async (params = {}) => {
  if (isBackendAvailable()) {
    try {
      const endpoint = params.all ? "/blogs/all" : "/blogs";
      const res = await client.get(endpoint, { params });
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getBlogsSync(params);
};

export const getBlog = async (idOrSlug) => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get(`/blogs/${idOrSlug}`);
      return res.data;
    } catch (e) {
      if (e.response && e.response.status === 404) {
        // Not found on backend
      } else {
        markBackendFailure();
      }
    }
  }
  const b = getBlogSync(idOrSlug);
  if (!b) {
    const err = new Error("Blog post not found");
    err.response = { status: 404, data: { detail: "Blog post not found" } };
    throw err;
  }
  return b;
};

export const createBlog = async (data) => {
  if (hasBackend) {
    try {
      const res = await client.post("/blogs", data);
      return res.data;
    } catch (e) {}
  }
  const blogs = getLocalBlogs();
  const slug = data.slug || slugify(data.title);
  const newBlog = {
    ...data,
    id: "blog-" + Date.now() + "-" + Math.random().toString(36).substr(2, 6),
    slug,
    published: data.published !== false,
    publishedAt: data.publishedAt || new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
    readTime: data.readTime || "5 min read",
    createdAt: new Date().toISOString(),
  };
  blogs.unshift(newBlog);
  saveLocalBlogs(blogs);
  return newBlog;
};

export const updateBlog = async (id, data) => {
  if (hasBackend) {
    try {
      const res = await client.put(`/blogs/${id}`, data);
      return res.data;
    } catch (e) {}
  }
  const blogs = getLocalBlogs();
  const index = blogs.findIndex((b) => b.id === id);
  if (index === -1) {
    const err = new Error("Blog not found");
    err.response = { status: 404, data: { detail: "Blog not found" } };
    throw err;
  }
  const updated = {
    ...blogs[index],
    ...data,
    slug: data.slug || slugify(data.title || blogs[index].title),
    updatedAt: new Date().toISOString(),
  };
  blogs[index] = updated;
  saveLocalBlogs(blogs);
  return updated;
};

export const deleteBlog = async (id) => {
  if (hasBackend) {
    try {
      const res = await client.delete(`/blogs/${id}`);
      return res.data;
    } catch (e) {}
  }
  let blogs = getLocalBlogs();
  blogs = blogs.filter((b) => b.id !== id);
  saveLocalBlogs(blogs);
  return { ok: true };
};

export const deleteAllBlogs = async () => {
  let backendResult = null;
  if (hasBackend) {
    try {
      const res = await client.delete("/blogs-all");
      backendResult = res.data;
    } catch (e) {}
  }
  saveLocalBlogs([]);
  return backendResult || { ok: true };
};

// ==================== CITY CONTENT API ====================

export const getCityContentSync = (state, citySlug) => {
  if (!state || !citySlug) return null;
  const all = getLocalCityContents();
  const s = String(state).toLowerCase().trim();
  const c = String(citySlug).toLowerCase().trim();
  const cUnslugged = c.replace(/-/g, " ");
  return (
    all.find((item) => {
      const itemState = String(item.state || "").toLowerCase().trim();
      const itemSlug = String(item.citySlug || "").toLowerCase().trim();
      const itemName = String(item.cityName || "").toLowerCase().trim();
      const itemSlugified = slugifyCity(item.cityName || "").toLowerCase().trim();
      return (
        itemState === s &&
        (itemSlug === c ||
          itemSlugified === c ||
          itemName === c ||
          itemName === cUnslugged ||
          itemSlug === slugifyCity(c))
      );
    }) || null
  );
};

export const getCityContent = async (state, citySlug) => {
  if (!state || !citySlug) return null;
  const s = String(state).toLowerCase().trim();
  const c = String(citySlug).toLowerCase().trim();

  if (isBackendAvailable()) {
    try {
      const res = await client.get(`/city-content/${s}/${c}`);
      if (res.data) {
        const all = getLocalCityContents();
        const idx = all.findIndex(
          (item) =>
            String(item.state || "").toLowerCase().trim() === s &&
            String(item.citySlug || "").toLowerCase().trim() === c
        );
        if (idx >= 0) {
          all[idx] = { ...all[idx], ...res.data };
        } else {
          all.unshift(res.data);
        }
        saveLocalCityContents(all);
        return res.data;
      }
    } catch (e) {
      if (e.response && e.response.status === 404) {
        return null;
      }
      markBackendFailure();
    }
  }
  return getCityContentSync(state, citySlug);
};

export const getAllCityContents = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/city-content");
      if (res.data && Array.isArray(res.data)) {
        saveLocalCityContents(res.data);
        return res.data;
      }
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalCityContents();
};

export const saveCityContent = async (state, citySlug, data) => {
  const s = String(state || "").toLowerCase().trim();
  const c = String(citySlug || "").toLowerCase().trim();
  let payload = {
    ...data,
    state: s,
    citySlug: c,
    cityName: data.cityName || c.replace(/-/g, " "),
    customTitle: (data.customTitle || "").trim(),
    leadParagraph: (data.leadParagraph || "").trim(),
    contentParagraphs: Array.isArray(data.contentParagraphs)
      ? data.contentParagraphs.filter(Boolean)
      : [],
    updatedAt: new Date().toISOString(),
  };

  if (hasBackend) {
    try {
      const res = await client.put(`/city-content/${s}/${c}`, payload);
      if (res.data) {
        payload = { ...payload, ...res.data };
      }
    } catch (e) {
      markBackendFailure();
    }
  }

  const all = getLocalCityContents();
  const idx = all.findIndex(
    (item) =>
      String(item.state || "").toLowerCase().trim() === s &&
      String(item.citySlug || "").toLowerCase().trim() === c
  );
  if (idx >= 0) {
    all[idx] = { ...all[idx], ...payload };
  } else {
    all.unshift(payload);
  }
  saveLocalCityContents(all);
  return payload;
};

export const deleteCityContent = async (state, citySlug) => {
  const s = String(state || "").toLowerCase().trim();
  const c = String(citySlug || "").toLowerCase().trim();

  if (hasBackend) {
    try {
      await client.delete(`/city-content/${s}/${c}`);
    } catch (e) {
      markBackendFailure();
    }
  }

  let all = getLocalCityContents();
  all = all.filter(
    (item) =>
      !(
        String(item.state || "").toLowerCase().trim() === s &&
        String(item.citySlug || "").toLowerCase().trim() === c
      )
  );
  saveLocalCityContents(all);
  return { ok: true };
};

// ==================== STATE CONTENT API ====================

export const getStateContentSync = (stateCode) => {
  if (!stateCode) return null;
  const all = getLocalStateContents();
  const s = String(stateCode).toLowerCase().trim();
  return all.find((item) => String(item.stateCode || "").toLowerCase().trim() === s) || null;
};

export const getStateContent = async (stateCode) => {
  if (!stateCode) return null;
  const s = String(stateCode).toLowerCase().trim();

  if (isBackendAvailable()) {
    try {
      const res = await client.get(`/state-content/${s}`);
      if (res.data) {
        const all = getLocalStateContents();
        const idx = all.findIndex((item) => String(item.stateCode || "").toLowerCase().trim() === s);
        if (idx >= 0) {
          all[idx] = { ...all[idx], ...res.data };
        } else {
          all.unshift(res.data);
        }
        saveLocalStateContents(all);
        return res.data;
      }
    } catch (e) {
      if (e.response && e.response.status === 404) {
        return null;
      }
      markBackendFailure();
    }
  }
  return getStateContentSync(stateCode);
};

export const getAllStateContents = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/state-content");
      if (res.data && Array.isArray(res.data)) {
        saveLocalStateContents(res.data);
        return res.data;
      }
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalStateContents();
};

export const saveStateContent = async (stateCode, data) => {
  const s = String(stateCode || "").toLowerCase().trim();
  let payload = {
    ...data,
    stateCode: s,
    stateName: data.stateName || "",
    customTitle: (data.customTitle || "").trim(),
    leadParagraph: (data.leadParagraph || "").trim(),
    contentParagraphs: Array.isArray(data.contentParagraphs)
      ? data.contentParagraphs.filter(Boolean)
      : [],
    updatedAt: new Date().toISOString(),
  };

  if (hasBackend) {
    try {
      const res = await client.put(`/state-content/${s}`, payload);
      if (res.data) {
        payload = { ...payload, ...res.data };
      }
    } catch (e) {
      markBackendFailure();
    }
  }

  const all = getLocalStateContents();
  const idx = all.findIndex((item) => String(item.stateCode || "").toLowerCase().trim() === s);
  if (idx >= 0) {
    all[idx] = { ...all[idx], ...payload };
  } else {
    all.unshift(payload);
  }
  saveLocalStateContents(all);
  return payload;
};

export const deleteStateContent = async (stateCode) => {
  const s = String(stateCode || "").toLowerCase().trim();

  if (hasBackend) {
    try {
      await client.delete(`/state-content/${s}`);
    } catch (e) {
      markBackendFailure();
    }
  }

  let all = getLocalStateContents();
  all = all.filter((item) => String(item.stateCode || "").toLowerCase().trim() !== s);
  saveLocalStateContents(all);
  return { ok: true };
};

// ==================== ABOUT US API ====================

export const getAboutContent = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/about");
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalAboutContent();
};

export const saveAboutContent = async (data) => {
  if (hasBackend) {
    try {
      const res = await client.put("/about", data);
      return res.data;
    } catch (e) {}
  }
  saveLocalAboutContent(data);
  return data;
};

// ==================== CONTACT US API ====================

export const getContactContent = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/contact");
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalContactContent();
};

export const saveContactContent = async (data) => {
  if (hasBackend) {
    try {
      const res = await client.put("/contact", data);
      return res.data;
    } catch (e) {}
  }
  saveLocalContactContent(data);
  return data;
};

// ==================== CONTACT MESSAGES API ====================

export const submitContactMessage = async (data) => {
  if (hasBackend) {
    try {
      const res = await client.post("/contact-messages", data);
      return res.data;
    } catch (e) {}
  }

  const msgs = getLocalContactMessages();
  const newMsg = {
    ...data,
    id: "msg-" + Date.now() + "-" + Math.random().toString(36).substr(2, 6),
    isRead: false,
    createdAt: new Date().toISOString(),
  };
  msgs.unshift(newMsg);
  saveLocalContactMessages(msgs);
  return newMsg;
};

export const getContactMessages = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/contact-messages");
      return res.data;
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalContactMessages();
};

export const toggleMessageRead = async (id) => {
  if (hasBackend) {
    try {
      const res = await client.put(`/contact-messages/${id}/read`);
      return res.data;
    } catch (e) {}
  }

  const msgs = getLocalContactMessages();
  const index = msgs.findIndex((m) => m.id === id);
  if (index !== -1) {
    msgs[index].isRead = !msgs[index].isRead;
    saveLocalContactMessages(msgs);
    return msgs[index];
  }
  return null;
};

export const deleteContactMessage = async (id) => {
  if (hasBackend) {
    try {
      const res = await client.delete(`/contact-messages/${id}`);
      return res.data;
    } catch (e) {}
  }

  let msgs = getLocalContactMessages();
  msgs = msgs.filter((m) => m.id !== id);
  saveLocalContactMessages(msgs);
  return { ok: true };
};

export const deleteAllContactMessages = async () => {
  let backendResult = null;
  if (hasBackend) {
    try {
      const res = await client.delete("/contact-messages-all");
      backendResult = res.data;
    } catch (e) {}
  }
  saveLocalContactMessages([]);
  return backendResult || { ok: true };
};

export const clearAllData = async () => {
  await deleteAllCourses();
  await deleteAllBlogs();
  await deleteAllContactMessages();
  return { ok: true };
};

// ==================== SEO & SCHEMA SETTINGS API ====================

export const getSeoSettings = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/seo-settings");
      if (res.data) {
        saveLocalSeoSettings(res.data);
        return res.data;
      }
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalSeoSettings();
};

export const saveSeoSettings = async (data) => {
  if (hasBackend) {
    try {
      const res = await client.put("/seo-settings", data);
      if (res.data) {
        saveLocalSeoSettings(res.data);
        return res.data;
      }
    } catch (e) {
      markBackendFailure();
    }
  }
  saveLocalSeoSettings(data);
  return data;
};

// ==================== LEGAL & POLICY API ====================

export const getPrivacyContent = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/privacy");
      if (res.data) {
        saveLocalPrivacyContent(res.data);
        return res.data;
      }
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalPrivacyContent();
};

export const savePrivacyContent = async (data) => {
  let updated = { ...data, updatedAt: new Date().toISOString() };
  if (hasBackend) {
    try {
      const res = await client.put("/privacy", data);
      if (res.data) updated = { ...updated, ...res.data };
    } catch (e) {
      markBackendFailure();
    }
  }
  saveLocalPrivacyContent(updated);
  return updated;
};

export const getTermsContent = async () => {
  if (isBackendAvailable()) {
    try {
      const res = await client.get("/terms");
      if (res.data) {
        saveLocalTermsContent(res.data);
        return res.data;
      }
    } catch (e) {
      markBackendFailure();
    }
  }
  return getLocalTermsContent();
};

export const saveTermsContent = async (data) => {
  let updated = { ...data, updatedAt: new Date().toISOString() };
  if (hasBackend) {
    try {
      const res = await client.put("/terms", data);
      if (res.data) updated = { ...updated, ...res.data };
    } catch (e) {
      markBackendFailure();
    }
  }
  saveLocalTermsContent(updated);
  return updated;
};

// ---------- Bulk Import Helpers ----------

export const bulkImportCourses = async (coursesList, mode = "append") => {
  if (hasBackend) {
    try {
      await client.post("/courses/bulk", { courses: coursesList, mode });
    } catch (e) {
      markBackendFailure();
    }
  }

  const existing = mode === "replace" ? [] : (getLocalCourses() || []);
  const now = new Date().toISOString();

  let merged;
  if (mode === "replace") {
    merged = coursesList.map((c, i) => {
      const slug = slugifyCity(c.name || `course-${i + 1}`);
      const citySlug = slugifyCity(c.city || "general");
      return {
        ...c,
        id: c.id || `course-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 7)}`,
        slug: c.slug || slug,
        citySlug: c.citySlug || citySlug,
        createdAt: c.createdAt || now,
      };
    });
  } else {
    const existingMap = new Map();
    existing.forEach((c) => {
      if (c.id) existingMap.set(`id:${c.id}`, c);
      const compKey = `comp:${(c.name || "").toLowerCase()}_${(c.state || "").toLowerCase()}_${(c.citySlug || slugifyCity(c.city || "")).toLowerCase()}_${(c.address || "").toLowerCase()}`;
      existingMap.set(compKey, c);
    });

    const resultList = [...existing];
    coursesList.forEach((c, i) => {
      const slug = slugifyCity(c.name || `course-${i + 1}`);
      const citySlug = slugifyCity(c.city || "general");
      const compKey = `comp:${(c.name || "").toLowerCase()}_${(c.state || "").toLowerCase()}_${citySlug}_${(c.address || "").toLowerCase()}`;
      const match = (c.id && existingMap.get(`id:${c.id}`)) || existingMap.get(compKey);
      if (match) {
        const idx = resultList.findIndex((item) => item.id === match.id);
        if (idx !== -1) {
          resultList[idx] = {
            ...match,
            ...c,
            id: match.id,
            slug: c.slug || match.slug || slug,
            citySlug: c.citySlug || match.citySlug || citySlug,
            updatedAt: now,
          };
        }
      } else {
        const newItem = {
          ...c,
          id: c.id || `course-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 7)}`,
          slug: c.slug || slug,
          citySlug: c.citySlug || citySlug,
          createdAt: c.createdAt || now,
        };
        resultList.push(newItem);
        existingMap.set(`id:${newItem.id}`, newItem);
        existingMap.set(compKey, newItem);
      }
    });
    merged = resultList;
  }

  saveLocalCourses(merged);
  return merged;
};

export const bulkImportCityContents = async (itemsList, mode = "append") => {
  if (hasBackend) {
    try {
      await client.post("/city-content/bulk", { items: itemsList, mode });
    } catch (e) {
      markBackendFailure();
    }
  }

  const existing = mode === "replace" ? [] : (getLocalCityContents() || []);
  const now = new Date().toISOString();
  const map = new Map();
  existing.forEach((item) => map.set(`${item.state.toLowerCase()}_${item.citySlug.toLowerCase()}`, item));

  itemsList.forEach((item) => {
    const state = item.state.toLowerCase();
    const citySlug = (item.citySlug || slugifyCity(item.cityName)).toLowerCase();
    const key = `${state}_${citySlug}`;
    map.set(key, { ...item, state, citySlug, updatedAt: now });
  });

  const merged = Array.from(map.values());
  saveLocalCityContents(merged);
  return merged;
};

export const bulkImportStateContents = async (itemsList, mode = "append") => {
  if (hasBackend) {
    try {
      await client.post("/state-content/bulk", { items: itemsList, mode });
    } catch (e) {
      markBackendFailure();
    }
  }

  const existing = mode === "replace" ? [] : (getLocalStateContents() || []);
  const now = new Date().toISOString();
  const map = new Map();
  existing.forEach((item) => map.set(item.stateCode.toLowerCase(), item));

  itemsList.forEach((item) => {
    const stateCode = item.stateCode.toLowerCase();
    map.set(stateCode, { ...item, stateCode, updatedAt: now });
  });

  const merged = Array.from(map.values());
  saveLocalStateContents(merged);
  return merged;
};

export const bulkImportBlogs = async (blogsList, mode = "append") => {
  if (hasBackend) {
    try {
      await client.post("/blogs/bulk", { blogs: blogsList, mode });
    } catch (e) {
      markBackendFailure();
    }
  }

  const existing = mode === "replace" ? [] : (getLocalBlogs() || []);
  const now = new Date().toISOString();
  let merged;
  if (mode === "replace") {
    merged = blogsList.map((b, i) => {
      const slug = b.slug || slugify(b.title || `article-${i + 1}`);
      return {
        ...b,
        id: b.id || `blog-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 6)}`,
        slug,
        createdAt: b.createdAt || now,
      };
    });
  } else {
    const map = new Map();
    existing.forEach((b) => map.set(b.slug || slugify(b.title), b));
    blogsList.forEach((b, i) => {
      const slug = b.slug || slugify(b.title || `article-${i + 1}`);
      const item = {
        ...b,
        id: b.id || `blog-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 6)}`,
        slug,
        createdAt: b.createdAt || now,
      };
      map.set(slug, item);
    });
    merged = Array.from(map.values());
  }

  saveLocalBlogs(merged);
  return merged;
};

export const bulkImportContactMessages = async (messagesList, mode = "append") => {
  if (hasBackend) {
    try {
      await client.post("/contact-messages/bulk", { messages: messagesList, mode });
    } catch (e) {
      markBackendFailure();
    }
  }

  const existing = mode === "replace" ? [] : (getLocalContactMessages() || []);
  const now = new Date().toISOString();
  const newItems = messagesList.map((m) => ({
    ...m,
    id: m.id || "msg-" + Date.now() + "-" + Math.random().toString(36).substr(2, 6),
    isRead: m.isRead || false,
    createdAt: m.createdAt || now,
  }));

  const merged = [...newItems, ...existing];
  saveLocalContactMessages(merged);
  return merged;
};




